import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, PieChart, TrendingUp, Calendar, Download, Filter,
  DollarSign, Users, Package, FileText, ArrowUp, ArrowDown,
  Activity, Target, Award, Zap, ChevronRight, Clock, RefreshCw,
  MapPin, Layers
} from 'lucide-react';
import { PageWrapper, Card, SectionTitle, StatCard } from '@/components/ui/PageWrapper';
import { Button } from '@/components/ui/button';
import { ResponsiveTable } from '@/components/ui/ResponsiveTable';
import { 
  BarChart, Bar, LineChart, Line, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { useReports } from '@/hooks/useReports';

export function ReportsPage() {
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const { data, loading, error, refresh } = useReports(dateRange);

  const pageActions = (
    <div className="flex flex-wrap gap-2">
      <select 
        value={dateRange}
        onChange={(e) => setDateRange(e.target.value as any)}
        className="px-3 sm:px-4 py-2 bg-zinc-700/50 rounded-xl text-sm sm:text-base text-white border border-zinc-600 focus:border-purple-500 focus:outline-none"
      >
        <option value="week">Ten tydzień</option>
        <option value="month">Ten miesiąc</option>
        <option value="quarter">Ten kwartał</option>
        <option value="year">Ten rok</option>
      </select>
      <Button onClick={refresh} variant="secondary" disabled={loading} className="text-sm sm:text-base">
        <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${loading ? 'animate-spin' : ''}`} />
        <span className="hidden sm:inline">Odśwież</span>
      </Button>
      <Button variant="primary" className="text-sm sm:text-base">
        <Download className="w-4 h-4 sm:w-5 sm:h-5" />
        <span className="hidden sm:inline">Eksportuj raport</span>
      </Button>
    </div>
  );

  if (loading && !data) {
    return (
      <PageWrapper 
        title="Raporty i Analizy" 
        subtitle="Kompleksowy przegląd wyników sprzedaży i statystyk"
        actions={pageActions}
      >
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper 
        title="Raporty i Analizy" 
        subtitle="Kompleksowy przegląd wyników sprzedaży i statystyk"
        actions={pageActions}
      >
        <Card className="p-12 text-center">
          <p className="text-red-400 mb-4">Błąd ładowania raportów: {error}</p>
          <Button onClick={refresh} variant="secondary">
            Spróbuj ponownie
          </Button>
        </Card>
      </PageWrapper>
    );
  }

  if (!data) return null;

  return (
    <PageWrapper 
      title="Raporty i Analizy" 
      subtitle="Kompleksowy przegląd wyników sprzedaży i statystyk"
      actions={pageActions}
    >
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StatCard
            icon={<DollarSign className="w-6 h-6" />}
            label="Przychód miesięczny"
            value={`${data.kpi.monthlyRevenue.toLocaleString('pl-PL')} zł`}
            trend={{ value: data.kpi.monthlyRevenueChange, isPositive: data.kpi.monthlyRevenueChange > 0 }}
            color="emerald"
            progress={data.kpi.targetRealization}
          />
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <StatCard
            icon={<FileText className="w-6 h-6" />}
            label="Liczba ofert"
            value={data.kpi.totalOffers.toString()}
            trend={{ value: data.kpi.offersChange, isPositive: data.kpi.offersChange > 0 }}
            color="purple"
            progress={Math.min(100, (data.kpi.totalOffers / 50) * 100)}
          />
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StatCard
            icon={<Target className="w-6 h-6" />}
            label="Konwersja"
            value={`${data.kpi.conversionRate.toFixed(1)}%`}
            trend={{ value: data.kpi.conversionChange, isPositive: data.kpi.conversionChange > 0 }}
            color="amber"
            progress={data.kpi.conversionRate}
          />
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label="Nowi klienci"
            value={data.kpi.newClients.toString()}
            trend={{ value: data.kpi.newClientsChange, isPositive: data.kpi.newClientsChange > 0 }}
            color="blue"
            progress={Math.min(100, (data.kpi.newClients / 20) * 100)}
          />
        </motion.div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Sales Trend */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-4 sm:p-6">
            <SectionTitle icon={<TrendingUp className="w-6 h-6" />}>
              Trend sprzedaży
            </SectionTitle>
            <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.salesTrend}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="period" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                  labelStyle={{ color: '#f3f4f6' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="sales" 
                  stroke="#8b5cf6" 
                  fillOpacity={1} 
                  fill="url(#colorSales)" 
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="#ef4444" 
                  strokeDasharray="5 5" 
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        {/* Product Distribution */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35 }}
        >
          <Card className="p-4 sm:p-6">
            <SectionTitle icon={<PieChart className="w-6 h-6" />}>
              Podział produktów
            </SectionTitle>
            <div className="h-[250px] sm:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie
                  data={data.productDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ category, percentage }) => `${category} ${percentage.toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {data.productDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                  formatter={(value: number) => `${value.toLocaleString('pl-PL')} zł`}
                />
              </RePieChart>
            </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Performance vs Target */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mb-6"
      >
        <Card className="p-4 sm:p-6">
          <SectionTitle icon={<Activity className="w-6 h-6" />}>
            Wyniki vs Cel
          </SectionTitle>
          <div className="h-[200px] sm:h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.performanceVsTarget}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="period" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                labelStyle={{ color: '#f3f4f6' }}
                formatter={(value: number) => `${value.toLocaleString('pl-PL')} zł`}
              />
              <Legend />
              <Bar dataKey="target" fill="#6b7280" name="Cel" />
              <Bar dataKey="actual" fill="#8b5cf6" name="Rzeczywiste" />
            </BarChart>
          </ResponsiveContainer>
          </div>
        </Card>
      </motion.div>

      {/* Regional Analysis */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="mb-6"
      >
        <Card className="p-6">
          <SectionTitle icon={<MapPin className="w-6 h-6" />}>
            Analiza regionalna
          </SectionTitle>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left p-4 text-gray-400 font-medium">Region</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Przychód</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Zamówienia</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Klienci</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Śr. wartość</th>
                </tr>
              </thead>
              <tbody>
                {data.regionalAnalysis.slice(0, 5).map((region) => (
                  <tr key={region.region} className="border-b border-zinc-700/50 hover:bg-zinc-800/30 transition-all">
                    <td className="p-4 font-medium text-white">{region.region}</td>
                    <td className="p-4 text-right font-mono text-white">
                      {region.revenue.toLocaleString('pl-PL', {
                        style: 'currency',
                        currency: 'PLN',
                        minimumFractionDigits: 0
                      })}
                    </td>
                    <td className="p-4 text-right text-gray-300">{region.orders}</td>
                    <td className="p-4 text-right text-gray-300">{region.clients}</td>
                    <td className="p-4 text-right text-gray-300">
                      {region.avgOrderValue.toLocaleString('pl-PL', {
                        style: 'currency',
                        currency: 'PLN',
                        minimumFractionDigits: 0
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Material Analysis */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mb-6"
      >
        <Card className="p-6">
          <SectionTitle icon={<Layers className="w-6 h-6" />}>
            Analiza materiałów
          </SectionTitle>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left p-4 text-gray-400 font-medium">Materiał</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Zużycie (m²)</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Przychód</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Zamówienia</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Śr. cena/m²</th>
                </tr>
              </thead>
              <tbody>
                {data.materialAnalysis.slice(0, 5).map((material) => (
                  <tr key={material.material} className="border-b border-zinc-700/50 hover:bg-zinc-800/30 transition-all">
                    <td className="p-4 font-medium text-white">{material.material}</td>
                    <td className="p-4 text-right text-gray-300">{material.usage.toFixed(2)}</td>
                    <td className="p-4 text-right font-mono text-white">
                      {material.revenue.toLocaleString('pl-PL', {
                        style: 'currency',
                        currency: 'PLN',
                        minimumFractionDigits: 0
                      })}
                    </td>
                    <td className="p-4 text-right text-gray-300">{material.orders}</td>
                    <td className="p-4 text-right text-gray-300">
                      {material.avgPrice.toLocaleString('pl-PL', {
                        style: 'currency',
                        currency: 'PLN',
                        minimumFractionDigits: 0
                      })}/m²
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Top Clients Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
      >
        <Card className="p-6">
          <SectionTitle 
            icon={<Award className="w-6 h-6" />}
            action={
              <Button variant="secondary" size="sm">
                Zobacz wszystkich
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            }
          >
            Top Klienci
          </SectionTitle>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="text-left p-4 text-gray-400 font-medium">Klient</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Przychód</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Zamówienia</th>
                  <th className="text-right p-4 text-gray-400 font-medium">Wzrost</th>
                </tr>
              </thead>
              <tbody>
                {data.topClients.map((client, index) => (
                  <motion.tr
                    key={client.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.05 }}
                    className="border-b border-zinc-700/50 hover:bg-zinc-800/30 transition-all"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        <span className="font-medium text-white">{client.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <span className="font-mono text-white">
                        {client.revenue.toLocaleString('pl-PL', {
                          style: 'currency',
                          currency: 'PLN',
                          minimumFractionDigits: 0
                        })}
                      </span>
                    </td>
                    <td className="p-4 text-right text-gray-300">{client.orders}</td>
                    <td className="p-4 text-right">
                      <span className={`flex items-center justify-end gap-1 ${
                        client.growth > 0 ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {client.growth > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                        {Math.abs(client.growth).toFixed(0)}%
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
      >
        <Card className="p-4 sm:p-6 bg-gradient-to-br from-purple-900/20 to-pink-900/20" gradient>
          <div className="flex items-center justify-between mb-4">
            <Zap className="w-8 h-8 text-yellow-400" />
            <span className="text-2xl font-bold text-white">
              {data.kpi.averageOrderValue.toLocaleString('pl-PL', {
                style: 'currency',
                currency: 'PLN',
                minimumFractionDigits: 0
              })}
            </span>
          </div>
          <h3 className="text-white font-semibold">Średnia wartość zamówienia</h3>
          <p className="text-sm text-gray-400 mt-1">
            {dateRange === 'week' ? 'W tym tygodniu' : 
             dateRange === 'month' ? 'W tym miesiącu' :
             dateRange === 'quarter' ? 'W tym kwartale' : 'W tym roku'}
          </p>
        </Card>

        <Card className="p-4 sm:p-6 bg-gradient-to-br from-emerald-900/20 to-teal-900/20" gradient>
          <div className="flex items-center justify-between mb-4">
            <Clock className="w-8 h-8 text-emerald-400" />
            <span className="text-2xl font-bold text-white">{data.kpi.averageProcessingTime.toFixed(1)} dni</span>
          </div>
          <h3 className="text-white font-semibold">Średni czas realizacji</h3>
          <p className="text-sm text-gray-400 mt-1">Od utworzenia do zaakceptowania</p>
        </Card>

        <Card className="p-4 sm:p-6 bg-gradient-to-br from-amber-900/20 to-orange-900/20" gradient>
          <div className="flex items-center justify-between mb-4">
            <Target className="w-8 h-8 text-amber-400" />
            <span className="text-2xl font-bold text-white">{data.kpi.targetRealization.toFixed(0)}%</span>
          </div>
          <h3 className="text-white font-semibold">Realizacja targetu</h3>
          <p className="text-sm text-gray-400 mt-1">
            {data.kpi.targetRealization > 100 ? 'Cel przekroczony!' : 'Do celu miesięcznego'}
          </p>
        </Card>
      </motion.div>

      {/* Time Trends */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mt-6"
      >
        <Card className="p-6">
          <SectionTitle icon={<Clock className="w-6 h-6" />}>
            Trendy czasowe
          </SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-4">
            <div className="text-center">
              <p className="text-gray-400 text-sm mb-2">Najlepszy dzień</p>
              <p className="text-2xl font-bold text-white">{data.timeTrends.bestDay}</p>
            </div>
            <div className="text-center">
              <p className="text-gray-400 text-sm mb-2">Najlepsza godzina</p>
              <p className="text-2xl font-bold text-white">{data.timeTrends.bestHour}:00</p>
            </div>
            <div className="text-center">
              <p className="text-gray-400 text-sm mb-2">Śr. czas odpowiedzi</p>
              <p className="text-2xl font-bold text-white">{data.timeTrends.avgResponseTime.toFixed(0)}h</p>
            </div>
            <div className="text-center">
              <p className="text-gray-400 text-sm mb-2">Śr. czas zamknięcia</p>
              <p className="text-2xl font-bold text-white">{data.timeTrends.avgClosingTime.toFixed(1)} dni</p>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageWrapper>
  );
}
